// The yes/no (`bool`) feature type on the frontend. Mirrors
// `backend/server/features/bool_feature.py` — see `docs/bool_features.md`.
//
// A stored bool is 1 or 0 in `decimal_value` (`key_decimal_value` for a map
// key half). The labels are display vocabulary only.

export const DEFAULT_TRUE_LABEL = 'yes'
export const DEFAULT_FALSE_LABEL = 'no'

// How a summary rolls one day's answers into the day's answer: `any` checkin
// saying the true label, or `all` of them. A summary setting, not a feature one.
export type BoolDayRule = 'any' | 'all'

export interface BoolParams {
  true_label?: string
  false_label?: string
}

const cleanLabel = (value: unknown, fallback: string): string =>
  typeof value === 'string' && value.trim() ? value.trim() : fallback

/** `[trueLabel, falseLabel]` for a bool sub-config's params, defaults filled. */
export const boolLabels = (params: BoolParams | null | undefined): [string, string] => [
  cleanLabel(params?.true_label, DEFAULT_TRUE_LABEL),
  cleanLabel(params?.false_label, DEFAULT_FALSE_LABEL),
]

/**
 * The label for a stored bool value. `null` for no value — unknown, never No.
 * A value outside {0, 1} (never written by the save path) renders as its raw
 * number rather than as either answer.
 */
export const boolLabelFor = (
  value: number | string | null | undefined,
  params: BoolParams | null | undefined,
): string | null => {
  if (value == null || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(n)) return String(value)
  const [trueLabel, falseLabel] = boolLabels(params)
  if (n === 1) return trueLabel
  if (n === 0) return falseLabel
  return String(n)
}

/** Whether a (sub-)config — scalar, list item, or map half — is a bool. */
export const isBoolSub = (config: { type?: string } | null | undefined): boolean =>
  config?.type === 'bool'

/** Whether a feature's config is a list of bools. */
export const isBoolList = (
  config: { type?: string; params?: { type?: string } } | null | undefined,
): boolean => config?.type === 'list' && config?.params?.type === 'bool'
